import { SortOrder } from 'utils'
import { Draggable } from 'components/simpletable/Simpletable'
import { DragItem } from 'components/simpletable/TableRow'
import { DragDirection } from './constants'

export const sortObjects = <T>(
    objects: T[] = [],
    sortedProperties: (keyof T)[],
    currentSortDirection: SortOrder
): T[] => {
    const firstObjectToSort = objects[0]
    if (firstObjectToSort) {
        sortedProperties.forEach((propertyKey) => {
            const firstValueToSortType =
                firstObjectToSort[propertyKey] instanceof Date
                    ? 'date'
                    : typeof firstObjectToSort[propertyKey]
            const sortFunction = resolveSortFunction(firstValueToSortType)

            objects = objects?.sort((a, b) => {
                return sortFunction(a, b, propertyKey, currentSortDirection)
            })
        })
    }
    return objects
}

const numberSort = <T>(
    a: T,
    b: T,
    propertyKey: keyof T,
    currentSortDirection: SortOrder
) => {
    const numberA = Number(a[propertyKey])
    const numberB = Number(b[propertyKey])
    return currentSortDirection === 'ascending'
        ? numberA - numberB
        : numberB - numberA
}

const stringSort = <T>(
    a: T,
    b: T,
    propertyKey: keyof T,
    currentSortDirection: SortOrder
) => {
    const stringA = String(a[propertyKey]).toUpperCase() // ignore upper and lowercase
    const stringB = String(b[propertyKey]).toUpperCase() // ignore upper and lowercase
    if (stringA < stringB) {
        return currentSortDirection === 'ascending' ? -1 : 1
    }
    if (stringA > stringB) {
        return currentSortDirection === 'ascending' ? 1 : -1
    }
    return 0
}

const dateSort = <T>(
    a: T,
    b: T,
    propertyKey: keyof T,
    currentSortDirection: SortOrder
) => {
    const dateA = a[propertyKey]
    const dateB = b[propertyKey]
    if (dateA < dateB) {
        return currentSortDirection === 'ascending' ? -1 : 1
    }
    if (dateA > dateB) {
        return currentSortDirection === 'ascending' ? 1 : -1
    }
    return 0
}

const resolveSortFunction = <T>(firstValueToSortType: keyof T) => {
    let sortFunction
    switch (firstValueToSortType) {
        case 'number':
            // Sort as a Number
            sortFunction = numberSort
            break
        case 'date':
            sortFunction = dateSort
            break
        case 'string':
        default:
            // Sort as a string
            sortFunction = stringSort
            break
    }
    return sortFunction
}

export const updateDraggedObjects = <T extends Draggable>(
    dragItem: DragItem,
    dropItem: DragItem,
    items: T[]
): T[] => {
    const dragDirection: DragDirection =
        dragItem.index < dropItem.index ? 'Down' : 'Up'

    return items.reduce((agg: T[], item) => {
        switch (dragDirection) {
            case 'Up':
                updateDraggedObjectUp(item, dragItem, dropItem)
                break
            case 'Down':
                updateDraggedObjectDown(item, dragItem, dropItem)
                break
        }
        return agg
    }, items)
}

const updateDraggedObjectUp = <T extends Draggable>(
    item: T,
    { index: dragIndex }: DragItem,
    { index: dropIndex }: DragItem
): T => {
    if (item.order === dragIndex) {
        item.order = dropIndex
    } else if (item.order < dropIndex) {
        // Do nothing!
    } else if (item.order > dragIndex) {
        // Do nothing!
    } else {
        item.order = item.order + 1
    }
    return item
}

const updateDraggedObjectDown = <T extends Draggable>(
    item: T,
    { index: dragIndex }: DragItem,
    { index: dropIndex }: DragItem
): T => {
    if (item.order === dragIndex) {
        item.order = dropIndex
    } else if (item.order < dragIndex) {
        // Do nothing!
    } else if (item.order > dropIndex) {
        // Do nothing!
    } else {
        item.order = item.order - 1
    }
    return item
}

export const isNumber = (string?: string): boolean => {
    return string !== undefined && !Number.isNaN(Number(string))
}

export const isNotEmptyString = (string?: string): boolean => {
    return string !== undefined && string !== ''
}

export const queryParamToNumber = (
    paramValue?: string[] | string,
    defaultValue = 0
): number => {
    if (!paramValue) return defaultValue
    return Number(paramValue.toString())
}

export const queryParamToString = (
    paramValue?: string[] | string
): string | null => {
    return paramValue?.toString() || null
}

export const queryParamToBoolean = (
    paramValue: string[] | string = 'false',
    defaultValue: boolean
): boolean => {
    return paramValue !== undefined
        ? paramValue.toString().toLowerCase() === 'false'
            ? false
            : true
        : defaultValue
}

export const strollToElement = (found?: Element | null) => {
    if (found && found instanceof HTMLElement) {
        window.scroll({ top: found.offsetTop - 100, behavior: 'smooth' })
    }
}

export const toAnchor = (text: string) =>
    text
        .replace(/\s/g, '_')
        .replace(/\?/g, '')
        .replace(/!/g, '')
        .replace(/##/g, '#')
        .replace(/\(/g, '')
        .replace(/\)/g, '')
